import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import type { ITelemetryItem } from '@microsoft/applicationinsights-core-js';
import { ApiError } from '@/models/apiError';
import { runtimeConfig } from '@/core/runtimeConfig';

export const reactPlugin = new ReactPlugin();

/**
 * Matches UUIDs (v1–v5) and long numeric IDs (12+ digits).
 * Compiled once at module scope for performance.
 */
const ID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|\b\d{12,}\b/gi;

/** Replaces all UUID and long numeric ID occurrences in a string with `<redacted>`. */
function scrubIds(value: string): string {
  return value.replace(ID_PATTERN, '<redacted>');
}

/** Redacts UUIDs and long numeric IDs from exception/message/custom-property strings before the item leaves the browser. */
export function piiScrubInitializer(item: ITelemetryItem): void {
  const baseData = item.baseData as Record<string, unknown> | undefined;

  // Scrub baseData.message (e.g. tracked message telemetry)
  if (baseData && typeof baseData['message'] === 'string') {
    baseData['message'] = scrubIds(baseData['message']);
  }

  // Scrub baseData.exceptions[*].message (exception telemetry)
  if (baseData && Array.isArray(baseData['exceptions'])) {
    for (const ex of baseData['exceptions'] as Array<Record<string, unknown>>) {
      if (typeof ex['message'] === 'string') {
        ex['message'] = scrubIds(ex['message']);
      }
    }
  }

  // Scrub top-level custom property string values
  if (item.data) {
    const data = item.data as Record<string, unknown>;
    for (const key of Object.keys(data)) {
      const val: unknown = data[key];
      if (typeof val === 'string') {
        data[key] = scrubIds(val);
      }
    }
  }
}

function buildClient(): ApplicationInsights | null {
  const conn = runtimeConfig.appInsightsConnectionString;
  if (conn === null) return null;

  const client = new ApplicationInsights({
    config: {
      connectionString: conn,
      extensions: [reactPlugin],
      enableAutoRouteTracking: false, // explicit per-route tracking via trackPageView
      disableCookiesUsage: true,
      disableFetchTracking: true, // BE owns request-level metrics via correlation-ID-joined logs
    },
  });
  client.loadAppInsights();
  client.addTelemetryInitializer(piiScrubInitializer);
  return client;
}

// Module-scope init — runs on first import, before first paint, once per page load.
const ai: ApplicationInsights | null = buildClient();

if (ai !== null) {
  const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    const correlationId = event.reason instanceof ApiError ? event.reason.correlationId : null;
    trackException(error, correlationId);
  };
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    });
  }
}

/** Tracks an exception in Application Insights; no-op if AI is not initialized. */
export function trackException(error: Error, correlationId: string | null): void {
  ai?.trackException({ exception: error, properties: { correlationId } });
}

/** Tracks a page view; no-op if AI is not initialized. */
export function trackPageView(properties: { name: string }): void {
  ai?.trackPageView(properties);
}
